import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import {
  useLayoutEffect,
  useState,
  type FC,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import {
  NavigationRailThumb,
  type NavigationRailItemBounds,
  type NavigationRailThumbPosition,
} from "./navigationRailThumb.js";

interface NavigationRailHeaderItemProps extends AriaButtonProps {
  icon: ReactNode;
  title: ReactNode;
  metadata?: ReactNode;
  /** Shows a chevron when set. */
  isExpanded?: boolean | undefined;
  /** Draws the rail from the icon down to the rows below. */
  hasRail?: boolean;
  className?: string;
  children?: ReactNode;
}

/** A timeline node beside its title, metadata, and optional disclosure. */
export const NavigationRailHeaderItem: FC<NavigationRailHeaderItemProps> = (
  props,
) => {
  const {
    icon,
    title,
    metadata,
    isExpanded,
    hasRail = false,
    className,
    children,
    ...buttonProps
  } = props;
  const hasChevron = isExpanded !== undefined;
  return (
    <AriaButton
      {...buttonProps}
      className={twMerge(
        "grid w-full grid-cols-[1.5rem_minmax(0,1fr)_1rem] items-start gap-x-2.5 gap-y-0.5 rounded-lg bg-card px-2.5 py-2.5 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        hasRail &&
          "relative before:pointer-events-none before:absolute before:top-5 before:bottom-0 before:left-[22px] before:w-px before:-translate-x-1/2 before:bg-border-strong before:content-['']",
        className,
      )}
      aria-expanded={isExpanded}
    >
      <span
        aria-hidden="true"
        className="relative col-start-1 row-span-2 row-start-1 -mt-0.5 flex size-6 items-center justify-center rounded-full bg-gray-100 text-muted-foreground"
      >
        {icon}
      </span>
      <span
        className={twMerge(
          "col-start-2 row-start-1 min-w-0 text-xs leading-5 font-semibold text-foreground",
          !hasChevron && "col-span-2",
        )}
      >
        {title}
      </span>
      {hasChevron &&
        (isExpanded ? (
          <IconChevronUp
            aria-hidden="true"
            className="col-start-3 row-start-1 mt-0.5 size-4 text-muted-foreground"
          />
        ) : (
          <IconChevronDown
            aria-hidden="true"
            className="col-start-3 row-start-1 mt-0.5 size-4 text-muted-foreground"
          />
        ))}
      {metadata != null && metadata !== false && (
        <span className="col-span-2 col-start-2 row-start-2 min-w-0 text-2xs font-normal text-muted-foreground">
          {metadata}
        </span>
      )}
      {children && (
        <span className="col-span-2 col-start-2 row-start-3 min-w-0">
          {children}
        </span>
      )}
    </AriaButton>
  );
};

interface NavigationRailProps {
  listRef: Ref<HTMLOListElement>;
  /** The rows, as `li` elements. */
  children: ReactNode;
  thumb: NavigationRailThumbPosition | undefined;
}

/**
 * Rows hung on a vertical line that continues the rail of the
 * `NavigationRailHeaderItem` above them. A thumb on the line marks the current
 * row.
 */
export const NavigationRail: FC<NavigationRailProps> = (props) => {
  const { listRef, children, thumb } = props;
  return (
    <div className="relative before:pointer-events-none before:absolute before:top-0 before:bottom-1 before:left-[22px] before:w-px before:-translate-x-1/2 before:bg-border-strong before:content-['']">
      <ol ref={listRef} className="py-2">
        {children}
      </ol>
      {thumb && <NavigationRailThumb thumb={thumb} />}
    </div>
  );
};

interface NavigationRailItemProps extends AriaButtonProps {
  className?: string;
  children: ReactNode;
}

/** A row on a `NavigationRail`. */
export const NavigationRailItem: FC<NavigationRailItemProps> = (props) => {
  const { className, children, ...buttonProps } = props;
  return (
    <div className="relative pr-2.5 pl-9">
      <AriaButton
        {...buttonProps}
        className={twMerge(
          "flex w-full items-start justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
          className,
        )}
      >
        {children}
      </AriaButton>
    </div>
  );
};

/** A rail row's item and where its row sits in the list. */
export interface NavigationRailMeasuredItem<
  T,
> extends NavigationRailItemBounds {
  item: T;
}

interface NavigationRailMeasuredItems<T> {
  rows: NavigationRailMeasuredItem<T>[];
  railHeight: number;
}

/**
 * Measures the rows of a `NavigationRail`'s list, which renders one `li` per
 * item in order, so the thumb can sit beside one.
 */
export function useNavigationRailItems<T>(
  listRef: RefObject<HTMLOListElement | null>,
  items: readonly T[],
): NavigationRailMeasuredItems<T> {
  const [rows, setRows] = useState<NavigationRailMeasuredItem<T>[]>([]);
  const [railHeight, setRailHeight] = useState(0);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const measuredRows = items.flatMap((item, index) => {
        const row = list.children.item(index);
        return row instanceof HTMLElement
          ? [{ item, top: row.offsetTop, height: row.offsetHeight }]
          : [];
      });
      setRows(measuredRows);
      setRailHeight(list.offsetHeight);
    };
    measure();
    // Remeasure when the pane resizes or titles wrap.
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [listRef, items]);

  return { rows, railHeight };
}
